// This file is auto-generated by @hey-api/openapi-ts

import { type Options, syncMaps, stopRemoteSync, getRemoteSync, startRemoteSync } from '../sdk.gen'
import { queryOptions, type UseMutationOptions } from '@tanstack/react-query'
import type {
  SyncMapsData,
  SyncMapsError2,
  SyncMapsResponse,
  StopRemoteSyncData,
  StopRemoteSyncError,
  StopRemoteSyncResponse,
  GetRemoteSyncData,
  StartRemoteSyncData,
  StartRemoteSyncError,
  StartRemoteSyncResponse,
} from '../types.gen'
import { client as _heyApiClient } from './initializer'

export type QueryKey<TOptions extends Options> = [
  Pick<TOptions, 'baseUrl' | 'body' | 'headers' | 'path' | 'query'> & {
    _id: string
    _infinite?: boolean
    tags?: ReadonlyArray<string>
  },
]

const createQueryKey = <TOptions extends Options>(
  id: string,
  options?: TOptions,
  infinite?: boolean,
  tags?: ReadonlyArray<string>
): [QueryKey<TOptions>[0]] => {
  const params: QueryKey<TOptions>[0] = {
    _id: id,
    baseUrl: options?.baseUrl || (options?.client ?? _heyApiClient).getConfig().baseUrl,
  } as QueryKey<TOptions>[0]
  if (infinite) {
    params._infinite = infinite
  }
  if (tags) {
    params.tags = tags
  }
  if (options?.body) {
    params.body = options.body
  }
  if (options?.headers) {
    params.headers = options.headers
  }
  if (options?.path) {
    params.path = options.path
  }
  if (options?.query) {
    params.query = options.query
  }
  return [params]
}

export const syncMapsQueryKey = (options?: Options<SyncMapsData>) => createQueryKey('syncMaps', options)

/**
 * Synchronize map data
 * Downloads the control data dump for the configured map schema from the backend and replaces the local map database. Runs synchronously: the request blocks until the sync finishes or the server side timeout (--timeout-s, 3600 s by default) elapses. By default the sync is skipped when the local data version matches the remote one or when the last sync is newer than the most recent configured sync time point.
 */
export const syncMapsOptions = (options?: Options<SyncMapsData>) => {
  return queryOptions({
    queryFn: async ({ queryKey, signal }) => {
      const { data } = await syncMaps({
        ...options,
        ...queryKey[0],
        signal,
        throwOnError: true,
      })
      return data
    },
    queryKey: syncMapsQueryKey(options),
  })
}

/**
 * Synchronize map data
 * Downloads the control data dump for the configured map schema from the backend and replaces the local map database. Runs synchronously: the request blocks until the sync finishes or the server side timeout (--timeout-s, 3600 s by default) elapses. By default the sync is skipped when the local data version matches the remote one or when the last sync is newer than the most recent configured sync time point.
 */
export const syncMapsMutation = (
  options?: Partial<Options<SyncMapsData>>
): UseMutationOptions<SyncMapsResponse, SyncMapsError2, Options<SyncMapsData>> => {
  const mutationOptions: UseMutationOptions<SyncMapsResponse, SyncMapsError2, Options<SyncMapsData>> = {
    mutationFn: async localOptions => {
      const { data } = await syncMaps({
        ...options,
        ...localOptions,
        throwOnError: true,
      })
      return data
    },
  }
  return mutationOptions
}

/**
 * Stop the remote sync window
 * Closes the remote sync window so that data sync targets the local NAS again.
 */
export const stopRemoteSyncMutation = (
  options?: Partial<Options<StopRemoteSyncData>>
): UseMutationOptions<StopRemoteSyncResponse, StopRemoteSyncError, Options<StopRemoteSyncData>> => {
  const mutationOptions: UseMutationOptions<
    StopRemoteSyncResponse,
    StopRemoteSyncError,
    Options<StopRemoteSyncData>
  > = {
    mutationFn: async localOptions => {
      const { data } = await stopRemoteSync({
        ...options,
        ...localOptions,
        throwOnError: true,
      })
      return data
    },
  }
  return mutationOptions
}

export const getRemoteSyncQueryKey = (options?: Options<GetRemoteSyncData>) => createQueryKey('getRemoteSync', options)

/**
 * Remote sync window status
 * Reports whether a remote sync window is currently active, together with the car to NAS data copy status read from the navigation API. An expired window is cleared and reported as inactive.
 */
export const getRemoteSyncOptions = (options?: Options<GetRemoteSyncData>) => {
  return queryOptions({
    queryFn: async ({ queryKey, signal }) => {
      const { data } = await getRemoteSync({
        ...options,
        ...queryKey[0],
        signal,
        throwOnError: true,
      })
      return data
    },
    queryKey: getRemoteSyncQueryKey(options),
  })
}

export const startRemoteSyncQueryKey = (options?: Options<StartRemoteSyncData>) =>
  createQueryKey('startRemoteSync', options)

/**
 * Start a remote sync window
 * Opens a remote sync window pointing at the configured VPN address, valid for sync.remote_ttl_m minutes. While the window is active, data sync targets the VPN address instead of the local NAS.
 */
export const startRemoteSyncOptions = (options?: Options<StartRemoteSyncData>) => {
  return queryOptions({
    queryFn: async ({ queryKey, signal }) => {
      const { data } = await startRemoteSync({
        ...options,
        ...queryKey[0],
        signal,
        throwOnError: true,
      })
      return data
    },
    queryKey: startRemoteSyncQueryKey(options),
  })
}

/**
 * Start a remote sync window
 * Opens a remote sync window pointing at the configured VPN address, valid for sync.remote_ttl_m minutes. While the window is active, data sync targets the VPN address instead of the local NAS.
 */
export const startRemoteSyncMutation = (
  options?: Partial<Options<StartRemoteSyncData>>
): UseMutationOptions<StartRemoteSyncResponse, StartRemoteSyncError, Options<StartRemoteSyncData>> => {
  const mutationOptions: UseMutationOptions<
    StartRemoteSyncResponse,
    StartRemoteSyncError,
    Options<StartRemoteSyncData>
  > = {
    mutationFn: async localOptions => {
      const { data } = await startRemoteSync({
        ...options,
        ...localOptions,
        throwOnError: true,
      })
      return data
    },
  }
  return mutationOptions
}
